import { useFormik } from 'formik';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Loader, Password, Text } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import fullLogo from '../../assets/logo_completo.png';
import backgroundImage from '../../assets/images/truck_backgorund.jpg';
import { RootState } from '../../redux/store';
import { LoginService } from '../../services/auth/loginService';

const ResetPasswordSchema = yup.object({
    password: yup.string().required('La contraseña es obligatoria').min(8, 'La contraseña debe tener al menos 8 caracteres').max(30, 'Lacontraseña no puede excederse de 30 caracteres').matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/,
        'La contraseña debe contener al menos una letra mayúscula, una minúscula y un número'
    ),
    password_confirmation: yup.string().required('La confirmación de contraseña obligatoria').oneOf([yup.ref('password'), ''], 'Contraseñas no coinciden'),
});

const ForgotPassword: FC = () => {

    // STATES 

    const [isLoading, setIsLoading] = useState(false);
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const userToken = new URLSearchParams(useLocation().search).get('token');

    // HOOKS

    const navigate = useNavigate();

    if (isAuthenticated) {
        setTimeout(() => {
            navigate('/users', { replace: true })
        }, 100);
    }

    // FUNCTIONS

    // --------------------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL RESETEO DE LA CONTRASEÑ
     * @EN HANDLES RESET PASSWORD
     * @param userToken 
     * @param password 
     * @param password_confirmation 
     */
    // --------------------------------------------------------------------------------------------------------------------------------------------
    const handleResetPassword = async (userToken: string, password: string, password_confirmation: string) => {
        setIsLoading(true);
        const response = await (await (new LoginService()).resetForgotPassword(userToken, password, password_confirmation)).getResponseData();

        if (!response.success) {
            toast.error(response.message);
            setIsLoading(false);
            return;
        }

        if (response.success) {
            try {
                navigate('/', { replace: true });
                toast.success('Contraseña actualizada con éxito');
            } catch (error) {
                toast.error('Error al actualizar la contraseña');
                return;
            } finally {
                setIsLoading(false);
            }
        }
    };
    // --------------------------------------------------------------------------------------------------------------------------------------------

    // FORMIK

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            password: '',
            password_confirmation: ''
        },
        validationSchema: ResetPasswordSchema,
        validateOnChange: false,
        onSubmit: (values) => {
            if (userToken && userToken.length > 10) {
                handleResetPassword(userToken, values.password, values.password_confirmation);
            } else {
                toast.error('Token inválido o nulo.');
            }
        },
    });

    // RENDER 

    return (
        <div className="relative min-h-screen flex items-center justify-center bg-black">
            <img src={backgroundImage} alt="Fondo" className="absolute inset-0 w-full h-full object-cover brightness-25 blur-sm z-0" />
            <div className="absolute top-0 right-0 w-full flex justify-center md:justify-start p-6 z-20">
                <img src={fullLogo} alt="Logo de Gema" className="w-32 md:w-40" />
            </div>
            <div className="relative login-shadow z-10 w-full max-w-md bg-white rounded-lg shadow-lg p-8 flex flex-col items-center mt-20 md:mt-0">
                <div className='text-center mb-5'>
                    <Text className="text-2xl font-bold text-black mt-5 mb-3">Actualiza tu contraseña</Text>
                    <Text className="text-sm text-gray-800">Por favor, introduce tu nueva contraseña.</Text>
                </div>
                <form onSubmit={formik.handleSubmit} className="w-full flex flex-col gap-5">
                    <div className="space-y-5">
                        <Password
                            id='password'
                            label="Contraseña"
                            placeholder="Introduce tu contraseña"
                            size="lg"
                            className="[&>label>span]:font-medium"
                            labelClassName='text-black text-base'
                            inputClassName="text-sm border border-black/70 rounded-md"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.errors.password}
                        />
                        <Password
                            id='password_confirmation'
                            label="Confirmar Contraseña"
                            placeholder="Confirma tu contraseña"
                            size="lg"
                            className="[&>label>span]:font-medium"
                            labelClassName='text-black text-base'
                            inputClassName="text-sm border border-black/70 rounded-md"
                            value={formik.values.password_confirmation}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.errors.password_confirmation}
                        />
                        <Button className="w-full bg-primary text-white hover:bg-primary/90" type="submit" size="lg" disabled={isLoading}>
                            {isLoading ? <Loader /> : 'Resetear contraseña'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;